import { css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import type { Commands } from '@vaadin/router';
import { LitPage } from '../components/lit-page';
import { isPuzzleId, type PuzzleId } from '../components/luna-common';
import { navigate } from '../navigation';
import {
  buildOptions,
  getCatalog,
  resetCatalog,
  resolveInitialValues,
  validateOptions,
  type GameOptionDefinition,
} from '../puzzles/settings-catalog';
import { encodePresetOptions } from '../puzzles/preset';
import {
  buildConfigOptionViews,
  deriveResumeCard,
  type ConfigOptionView,
  type ResumeCardData,
} from '../puzzles/config-options';
import type { Options } from '../puzzles/wasmBridge/viewModel';

import '../components/luna-button/luna-button';
import '../components/luna-icon/luna-icon';
import '../components/luna-stepper/luna-stepper';
import '../components/luna-control-toggle/luna-control-toggle';
import '../components/luna-control-choices/luna-control-choices';
import '../components/luna-skeleton/luna-skeleton';

type ConfigState = 'loading' | 'ready' | 'empty' | 'error';

const STORAGE_PREFIX = 'last-custom-config-';

@customElement('app-config')
export class AppConfig extends LitPage {
  @state() private _puzzleName: PuzzleId = 'map';
  @state() private _state: ConfigState = 'loading';
  @state() private _views: ConfigOptionView[] = [];
  @state() private _resume: ResumeCardData | null = null;
  @state() private _errorMessage = '';

  private _definitions: GameOptionDefinition[] = [];
  private _values = new Map<number, number | boolean>();

  private get storageKey(): string {
    return `${STORAGE_PREFIX}${this._puzzleName}`;
  }

  onBeforeEnter(
    location: { params: { puzzleName?: string } },
    commands: Commands
  ): void {
    const name = location.params.puzzleName ?? '';
    if (!isPuzzleId(name)) {
      commands.redirect('/');
      return;
    }
    this._puzzleName = name;
  }

  async firstUpdated() {
    await this.load();
  }

  private async load(): Promise<void> {
    this._state = 'loading';

    const catalog = await getCatalog();
    if (catalog === null) {
      this._state = 'error';
      return;
    }

    const definitions = catalog[this._puzzleName] ?? [];
    if (definitions.length === 0) {
      this._state = 'empty';
      return;
    }

    this._definitions = definitions;
    const saved = this.readSavedOptions();
    this._resume = deriveResumeCard(this._puzzleName, definitions, saved);
    this._values = resolveInitialValues(definitions, []);
    this._views = buildConfigOptionViews(definitions, this._values);
    this._state = 'ready';
  }

  private retry(): void {
    resetCatalog();
    void this.load();
  }

  private readSavedOptions(): Options[] | null {
    try {
      const raw = localStorage.getItem(this.storageKey);
      return raw ? (JSON.parse(raw) as Options[]) : null;
    } catch {
      return null;
    }
  }

  private writeSavedOptions(options: Options[]): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(options));
    } catch {
      // Storage failure degrades silently (BR-73).
    }
  }

  private onValueChange(definition: GameOptionDefinition, event: Event): void {
    const value = (event as CustomEvent<{ value: number | boolean }>).detail.value;
    this._values.set(definition.index, value);
    this._errorMessage = '';
    this._views = buildConfigOptionViews(this._definitions, this._values);
  }

  private onResume(): void {
    if (!this._resume) {
      return;
    }
    navigate(`/puzzles/${this._puzzleName}?custom=${encodePresetOptions(this._resume.options)}`);
  }

  private onPlay(): void {
    const options = buildOptions(this._definitions, this._values);
    const { valid, errors } = validateOptions(this._definitions, options);
    if (!valid) {
      this._errorMessage = errors.join(' ');
      return;
    }
    this._errorMessage = '';

    this.writeSavedOptions(options);
    navigate(`/puzzles/${this._puzzleName}?custom=${encodePresetOptions(options)}`);
  }

  private onCancel(): void {
    navigate(`/puzzles/${this._puzzleName}/boards`);
  }

  content() {
    return html`
      <main class="app-config app-config--${this._state}">
        ${this.renderBody()}
      </main>
    `;
  }

  private renderBody() {
    switch (this._state) {
      case 'loading':
        return html`<luna-skeleton
          class="app-config__skeleton"
          aria-hidden="true"
        ></luna-skeleton>`;
      case 'error':
        return html`
          <div class="app-config__message" role="alert">
            <p class="mbw-type-body-large">Something went wrong loading the options.</p>
            <luna-button variant="primary" @click=${this.retry}>Try again</luna-button>
          </div>
        `;
      case 'empty':
        return html`
          <div class="app-config__message">
            <p class="mbw-type-body-large">This puzzle has no configurable options.</p>
          </div>
        `;
      case 'ready':
        return this.renderConfig();
    }
  }

  private renderConfig() {
    return html`
      <div class="app-config__col">
        ${this._resume ? this.renderResumeCard() : ''}
        ${this._resume
          ? html`<div class="app-config__divider-label mbw-type-body-small">or start a new one</div>`
          : ''}
        <div class="app-config__card">
          ${this._views.map((view) => this.renderControl(view))}
          <div class="app-config__error" role="alert" ?hidden=${!this._errorMessage}>
            ${this._errorMessage}
          </div>
          <div class="app-config__actions">
            <luna-button variant="primary" icon="play" full @click=${this.onPlay}>Play</luna-button>
            <luna-button variant="neutral" full @click=${this.onCancel}>Cancel</luna-button>
          </div>
        </div>
      </div>
    `;
  }

  private renderResumeCard() {
    return html`
      <div class="app-config__resume">
        <luna-icon class="app-config__resume-icon" name="timer" size="28" aria-hidden="true"></luna-icon>
        <div class="app-config__resume-copy">
          <span class="mbw-type-eyebrow">Game in progress</span>
          <span class="app-config__resume-label">${this._resume!.label}</span>
        </div>
        <luna-button variant="primary" icon="play" @click=${this.onResume}>Resume</luna-button>
      </div>
    `;
  }

  private renderControl(view: ConfigOptionView) {
    const { definition, kind, value } = view;

    switch (kind) {
      case 'number':
        return html`
          <luna-stepper
            label=${definition.name}
            .min=${definition.min ?? 0}
            .max=${definition.max ?? 100}
            .value=${value as number}
            @luna-change=${(event: Event) => this.onValueChange(definition, event)}
          ></luna-stepper>
        `;
      case 'boolean':
        return html`
          <luna-control-toggle
            label=${definition.name}
            .value=${value as boolean}
            @luna-change=${(event: Event) => this.onValueChange(definition, event)}
          ></luna-control-toggle>
        `;
      case 'choices':
        return html`
          <luna-control-choices
            label=${definition.name}
            .choices=${definition.choices ?? []}
            .value=${value as number}
            @luna-change=${(event: Event) => this.onValueChange(definition, event)}
          ></luna-control-choices>
        `;
    }
  }

  styles() {
    return css`
      .app-config {
        box-sizing: border-box;
        display: flex;
        justify-content: center;
        padding: 24px 16px 40px;
      }

      .app-config__col {
        display: flex;
        flex-direction: column;
        gap: 22px; /* pinned: figma Config gap */
        width: 640px;
        max-width: 100%;
      }

      .app-config__resume {
        display: flex;
        align-items: center;
        gap: 16px;
        box-sizing: border-box;
        width: 100%;
        min-height: 96px;
        padding: 16px 20px;
        border-radius: 16px; /* pinned: figma Config resume-card */
        background-image: linear-gradient(180deg, #ffffff 0%, #f3f7ff 100%); /* pinned: figma Config resume-card */
        box-shadow: inset 0 0 0 1px var(--mbw-card-hairline), 0 3px 0 var(--mbw-card-ledge);
      }

      .app-config__resume-icon {
        color: var(--cds-blue-60);
        flex-shrink: 0;
      }

      .app-config__resume-copy {
        display: flex;
        flex-direction: column;
        gap: 4px;
        flex: 1;
        min-width: 0;
      }

      .app-config__resume-copy .mbw-type-eyebrow {
        color: var(--mbw-text-muted);
      }

      .app-config__resume-label {
        font-family: var(--mbw-font-sans);
        font-weight: 700;
        font-size: 20px; /* pinned: figma Config resume label */
        line-height: 24px;
        color: var(--cds-text-primary);
      }

      .app-config__divider-label {
        margin: 0;
        text-align: center;
        color: var(--mbw-text-faint);
      }

      .app-config__card {
        display: flex;
        flex-direction: column;
        gap: 22px;
        box-sizing: border-box;
        padding: 24px; /* pinned: figma Config card pad */
        border-radius: var(--luna-radius-card);
        background: var(--mbw-surface-card);
        box-shadow: inset 0 0 0 1px var(--mbw-card-hairline), 0 3px 0 var(--mbw-card-ledge);
      }

      .app-config__card luna-stepper,
      .app-config__card luna-control-toggle,
      .app-config__card luna-control-choices {
        width: 100%;
      }

      .app-config__error {
        color: #c8392b; /* same treatment as the auth screens' .auth-error */
        font-weight: 600;
      }

      .app-config__error[hidden] {
        display: none;
      }

      .app-config__actions {
        display: flex;
        flex-direction: column;
        gap: 10px;
        width: 100%;
      }

      .app-config__actions luna-button {
        display: block;
        width: 100%;
      }

      .app-config__skeleton {
        width: 640px;
        max-width: 100%;
        height: 320px;
      }

      .app-config__message {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 16px;
        padding: 48px 24px;
        text-align: center;
      }

      .app-config__message p {
        margin: 0;
        color: var(--mbw-text-body);
      }

      @media (max-width: 767px) {
        .app-config {
          padding: 16px 16px 24px;
        }

        .app-config__resume {
          flex-direction: column;
          align-items: stretch;
          text-align: left;
        }

        .app-config__resume luna-button {
          width: 100%;
        }
      }
    `;
  }
}
